import { Button } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { LeadMatch } from "../../../Leads/api/services.types.ts"
import { LeadWizardDuplicateRow } from "./LeadWizardDuplicateRow.tsx"
import { LeadWizardMatchPanel } from "./LeadWizardMatchPanel.tsx"
import { type CustomerMatchCandidate, candidateKey } from "./customer-match.ts"
import { type UnifiedDuplicateMatch, isStrongRow } from "./duplicate-match.ts"

// Ranked strongest-first, so the tail is the part worth dropping. One row past
// this bounds the payload the wizard asks for, so a fuller list is itself the
// signal that more exist.
const MAX_VISIBLE_UNIFIED_MATCHES = 5

interface LeadWizardDuplicateWarningProps {
  rows: UnifiedDuplicateMatch[]
  onUseExisting: (candidate: CustomerMatchCandidate) => void
  onCreateNewCustomer: () => void
  onOpenExistingLead: (lead: LeadMatch) => void
  onCreateAnyway: () => void
  busyKey?: string | null
}

/**
 * The single duplicate-resolution card. Every row already carries exactly one
 * outcome — merged rows resolve as leads, unmerged customer rows resolve as
 * customers — so the shell only has to decide the panel's own words and
 * whether a generic "start fresh" escape belongs under the list: it does only
 * when nothing here is a lead risk, since a row with a lead already offers its
 * own Create Anyway.
 */
export function LeadWizardDuplicateWarning({
  rows,
  onUseExisting,
  onCreateNewCustomer,
  onOpenExistingLead,
  onCreateAnyway,
  busyKey
}: LeadWizardDuplicateWarningProps) {
  const { t } = useTranslation()

  if (!rows.length) return null

  // A strong (phone/email) primary is confirmed enough to be *the* answer, so
  // it renders alone — every weaker or unrelated row behind it would only
  // restate the same question with its own competing set of actions, which is
  // exactly what used to render as several stacked duplicate warnings. A weak
  // (name-only) primary has no such confidence, so the list still helps the
  // user pick the right person out of a crowd who shares a name.
  const strongPrimary = isStrongRow(rows[0])
  const visible = strongPrimary ? [rows[0]] : rows.slice(0, MAX_VISIBLE_UNIFIED_MATCHES)
  const hasMore = !strongPrimary && rows.length > visible.length
  const leadBearing = rows[0].kind !== "customer"

  const title = leadBearing
    ? t("leadWizard.leadDuplicate.title")
    : t("leadWizard.duplicate.title")
  const description = leadBearing
    ? t(
        rows[0].kind === "merged"
          ? "leadWizard.leadDuplicate.mergedDescription"
          : "leadWizard.leadDuplicate.description"
      )
    : t("leadWizard.duplicate.description")

  return (
    <LeadWizardMatchPanel
      title={title}
      description={description}
      more={hasMore ? t("leadWizard.duplicate.more") : undefined}
      footer={
        !leadBearing ? (
          <Button size={"small"} type={"link"} onClick={onCreateNewCustomer}>
            {t("leadWizard.duplicate.createNew")}
          </Button>
        ) : undefined
      }
    >
      {visible.map((row) => (
        <LeadWizardDuplicateRow
          key={row.key}
          match={row}
          busy={
            row.kind !== "lead" && busyKey === candidateKey(row.customer)
          }
          onUseExisting={onUseExisting}
          onOpenExisting={onOpenExistingLead}
          onCreateAnyway={onCreateAnyway}
        />
      ))}
    </LeadWizardMatchPanel>
  )
}

export { MAX_VISIBLE_UNIFIED_MATCHES }
